import { SearchBar, Pagination, Icon, Checkbox, Button } from "@ab-inbev-maz/compass";
import { useState, useRef, useEffect } from "react";
import EmptyTable from "./EmptyTable";

interface MosaicItem {
  id: string | number;
  thumbnail?: string;
  start_date?: string | Date;
  name?: string;
  combo_id?: string;
}

interface MosaicTableProps {
  title?: string;
  items?: MosaicItem[];
  loading?: boolean;
  onSearch?: (search: string) => void;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  currentPage?: number;
  itemsPerPage?: number;
  totalItems?: number;
  initialSearchValue?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  labels?: {
    id?: string;
    start_date?: string;
  };
  onItemClick?: (item: MosaicItem) => void;
  onDownloadSelection?: (items: MosaicItem[]) => void;
  onDownloadAll?: (items: MosaicItem[]) => void;
}

const checkboxStyle = {
  '--checkbox-border': "2px solid #A6A6A6"
}

const buttonStyle = {
  '--button-padding': "0.25rem 0.5rem 0.25rem 1rem",
  '--button-md-padding': "0.25rem 0.5rem 0.25rem 1rem",
}

const MosaicTable = ({
  title = "Listado de Elementos",
  items = [],
  onSearch,
  onPageChange,
  onItemsPerPageChange,
  currentPage = 1,
  itemsPerPage = 25,
  totalItems = 0,
  initialSearchValue = "",
  emptyMessage = "No hay elementos disponibles",
  searchPlaceholder = "Buscar elemento",
  labels = {
    id: "ID",
    start_date: "Vigencia"
  },
  onItemClick,
  onDownloadSelection,
  onDownloadAll
}: MosaicTableProps) => {

  const [loading, setLoading] = useState(false);

  // Referencia al botón de descarga de selección y a los items seleccionados
  const checkedItems = useRef<Set<MosaicItem>>(new Set());
  const downloadSelectionRef = useRef<HTMLButtonElement>(null);

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const handleCardClick = (item: MosaicItem, event: React.MouseEvent<HTMLDivElement>) => {
    const isActions = (event.target as HTMLElement).closest(".actions");
    if (isActions) return;
    onItemClick?.(item);
  };

  const handleCardKeyDown = (item: MosaicItem, event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const isActions = (event.target as HTMLElement).closest(".actions");
      if (isActions) return;
      onItemClick?.(item);
    }
  };

  const handleCheckboxChange = (item: MosaicItem, event: React.ChangeEvent<HTMLInputElement>) => {
    if (!item.thumbnail) return;

    if (event.target.checked) {
      checkedItems.current.add(item);
    } else {
      checkedItems.current.delete(item);
    }

    toggleDisabled(!(checkedItems.current.size > 0));
  };

  const toggleDisabled = (disabled: boolean) => {
    if (disabled) {
      downloadSelectionRef.current?.setAttribute('disabled', '');
    } else {
      downloadSelectionRef.current?.removeAttribute('disabled');
    }
  };

  const handleDownloadSelection = async () => {
    setLoading(true);
    if (checkedItems.current.size > 0) toggleDisabled(true);
    const items = Array.from(checkedItems.current);
    await onDownloadSelection?.(items);
    setLoading(false);
    if (checkedItems.current.size > 0) toggleDisabled(false);
  };

  const handleDownloadAll = async () => {
    setLoading(true);
    await onDownloadAll?.(items);
    setLoading(false);
  };

  const formatDate = (value: string | Date) => {
    if (typeof value === 'string') {
      const date = new Date(value);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } else if (value instanceof Date) {
      return value.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
    return value;
  };

  useEffect(() => {
    toggleDisabled(!(checkedItems.current.size > 0));
  }, []);

  return (
    <>
      <div className="rounded-lg p-4 gap-6">
        <div className="p-4 border-b rounded-t-lg bg-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium">{title}</h2>

            <div className="flex items-center gap-4">
              <Button ref={downloadSelectionRef} variant="secondary" style={buttonStyle as React.CSSProperties} onClick={handleDownloadSelection}>
                <p className="text-xs font-medium">Descargar <br />selección</p>
                <div className="w-6 h-6 p-0.5">
                  <Icon name="arrow/arrow-right" />
                </div>
              </Button>
              <Button variant="primary" style={buttonStyle as React.CSSProperties} onClick={handleDownloadAll} disabled={loading}>
                <p className="text-xs font-medium">Descargar <br />todo</p>
                <div className={`w-6 h-6 p-0.5 rounded-full text-black ${loading ? 'opacity-50' : 'bg-[#FFCC00]'}`}>
                  <Icon name="arrow/download" />
                </div>
              </Button>
            </div>
          </div>
        </div>

        {items.length === 0 ? (
          <EmptyTable message={emptyMessage} />
        ) : (
          <>
            {/* Grid de tarjetas */}
            <div className="py-4">
              <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,180px))] gap-4 w-full justify-center">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`border p-2 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow max-w-[180px] relative h-fit ${onItemClick ? 'cursor-pointer' : ''}`}
                    onClick={handleCardClick.bind(null, item)}
                    onKeyDown={handleCardKeyDown.bind(null, item)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Ver detalles de ${item.id}`}
                  >
                    {/* Acciones */}
                    <div className="flex gap-2 absolute top-2 left-2 z-10 p-2 actions">
                      <div className="w-6 h-6 bg-white shadow-sm rounded-xs flex items-center justify-center" style={checkboxStyle as React.CSSProperties}>
                        <Checkbox
                          value={item.id}
                          onChange={handleCheckboxChange.bind(null, item)}
                        />
                      </div>
                      <button className="bg-white shadow-sm w-6 h-6 rounded-xs flex items-center justify-center cursor-pointer hover:bg-gray-100" onClick={() => onDownloadSelection?.([item])}>
                        <div className="h-4 w-4">
                          <Icon name="arrow/download" />
                        </div>
                      </button>
                    </div>


                    {/* Card header con imagen */}
                    <div className="relative rounded-lg">
                      <img
                        src={item.thumbnail}
                        alt="Preview"
                        className="inset-0 w-full h-full object-contain rounded-lg"
                      />
                    </div>

                    {/* Card content */}
                    <div className="mt-3">
                      {
                        item.id && (
                          <>
                            <h3 className="text-sm font-medium text-gray-600">{labels.id === '' ? item.name : item.combo_id}</h3>
                            <p className="text-xs text-gray-400 mb-3">{labels.id}</p>
                          </>
                        )
                      }

                      {item.start_date && (
                        <>
                          <h3 className="text-sm font-medium text-gray-600">
                            {formatDate(item.start_date)}
                          </h3>
                          <p className="text-xs text-gray-400">{labels.start_date}</p>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer con paginación */}
            <div className="p-4 flex items-center justify-end">

              {/* Paginación */}
              <Pagination
                className="flex-row! !flex-nowrap"
                page={currentPage}
                total={totalItems}
                size={itemsPerPage}
                sizes={[
                  { label: 'Filas por página: 25', value: 25 },
                  { label: 'Filas por página: 50', value: 50 },
                  { label: 'Filas por página: 100', value: 100 },
                ]}
                onChangePage={handlePageChange}
                onChangeSize={(newSize: number) => onItemsPerPageChange && onItemsPerPageChange(newSize)}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default MosaicTable;
